import React, { createContext, useContext, useState, useCallback } from "react";

const DialogContext = createContext();

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error("useDialog must be used within a DialogProvider");
    }
    return context;
};

export const DialogProvider = ({ children }) => {
    const [dialog, setDialog] = useState({
        isOpen: false,
        type: "alert", // alert, confirm, prompt
        title: "",
        message: "",
        confirmText: "OK",
        cancelText: "Cancel",
        onConfirm: null,
        onCancel: null,
        defaultValue: "",
    });

    const showAlert = useCallback((message, title = "Alert") => {
        return new Promise((resolve) => {
            setDialog({
                isOpen: true,
                type: "alert",
                title,
                message,
                confirmText: "OK",
                onConfirm: () => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(true);
                },
            });
        });
    }, []);

    const showConfirm = useCallback((message, title = "Confirm", confirmText = "Confirm", cancelText = "Cancel") => {
        return new Promise((resolve) => {
            setDialog({
                isOpen: true,
                type: "confirm",
                title,
                message,
                confirmText,
                cancelText,
                onConfirm: () => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(true);
                },
                onCancel: () => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(false);
                },
            });
        });
    }, []);

    const showPrompt = useCallback((message, defaultValue = "", title = "Prompt") => {
        return new Promise((resolve) => {
            setDialog({
                isOpen: true,
                type: "prompt",
                title,
                message,
                defaultValue,
                confirmText: "Submit",
                cancelText: "Cancel",
                onConfirm: (value) => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(value);
                },
                onCancel: () => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(null);
                },
            });
        });
    }, []);

    return (
        <DialogContext.Provider value={{ showAlert, showConfirm, showPrompt, dialog }}>
            {children}
        </DialogContext.Provider>
    );
};
